import clsx from 'clsx';
import { pick } from 'lodash-es';
import { Dispatch, ReactElement, useCallback, useContext, useEffect, useRef } from 'react';
import { Rnd } from 'react-rnd';

import { PanelContext } from './PanelContext';
import { PanelInstanceContext } from './PanelInstanceContext';
import { PanelPosition, PanelSize } from './types';
import { PanelProps } from './types/PanelProps';
import { useSaveStateIn } from './usePanelStateHandler';

export function PopinWindow({
  children,
  className,
  maxHeight,
  maxWidth,
  minHeight,
  minWidth,
  modal,
  position = modal ? 'center' : 'tile',
  resizeable = modal !== true,
  size = { height: 200, width: 320 },
  panelPosition,
  setPanelPosition,
  panelSize,
  setPanelSize,
  setInitialPanelSize,
  hidden,
}: PanelProps & {
  panelPosition: PanelPosition;
  setPanelPosition: Dispatch<PanelPosition>;
  panelSize: PanelSize;
  setPanelSize: Dispatch<PanelSize>;
  setInitialPanelSize: Dispatch<PanelSize>;
  hidden?: boolean;
}): ReactElement {
  const panelRef = useRef<Rnd>(null);

  const { bounds, zIndex, bringPanelToFront, uniqueId } = useContext(PanelInstanceContext);

  const centerWindow = useCallback(() => {
    const b = panelRef.current?.getSelfElement()?.getBoundingClientRect();
    let height = b?.height ?? size.height;
    // You can't auto height a centered panel
    if (height === 'auto') {
      height = 400;
    }
    setPanelPosition({
      x: Math.max((window.innerWidth - (b?.width ?? size.width)) / 2, 0),
      y: Math.max((window.innerHeight - height) / 2, 0),
    });
  }, [setPanelPosition, size.height, size.width]);

  useEffect(() => {
    if (position === 'center' && !resizeable) {
      centerWindow();

      window.addEventListener('resize', centerWindow);
      return () => window.removeEventListener('resize', centerWindow);
    }
    return;
  }, [centerWindow, position, resizeable]);

  const resizePanel = useCallback(
    (newPanelSize: Partial<PanelSize>) => {
      const newSize = { ...panelSize, ...newPanelSize };
      if (newSize.width !== panelSize.width || newSize.height !== panelSize.height) {
        setPanelSize(newSize);
      }
    },
    [panelSize, setPanelSize],
  );

  const initialResizePanel = useCallback(
    (newPanelSize: Partial<PanelSize>) => {
      const newSize = { ...panelSize, ...newPanelSize };
      if (newSize.width !== panelSize.width || newSize.height !== panelSize.height) {
        setInitialPanelSize(newSize);
      }
    },
    [panelSize, setInitialPanelSize],
  );

  const saveStateIn = useSaveStateIn({ uniqueId });

  useEffect(() => {
    saveStateIn({
      panelPosition,
      panelSize,
    });
  }, [panelPosition, panelSize, saveStateIn]);

  return (
    <PanelContext.Provider value={{ resizePanel, initialResizePanel, resizeable }}>
      {modal && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            backgroundColor: 'rgb(0,0,0,0.1)',
            zIndex,
          }}
        />
      )}
      <Rnd
        ref={panelRef}
        className={clsx('WindowPanel', className)}
        dragHandleClassName={'draggable-handle'}
        maxHeight={maxHeight}
        maxWidth={maxWidth}
        minWidth={minWidth}
        minHeight={minHeight}
        position={panelPosition}
        size={panelSize.height === 'auto' ? (pick(panelSize, 'width') as PanelSize) : panelSize}
        style={{ zIndex, visibility: hidden ? 'hidden' : undefined }}
        disableDragging={!resizeable}
        enableResizing={resizeable}
        bounds={bounds ?? document.body}
        onDragStop={(_evt, data) => {
          setPanelPosition({ x: data.x, y: data.y });
        }}
        onResizeStop={(_evt, _dir, ref, _delta, position) => {
          setPanelPosition(position);
          setPanelSize({
            width: parseInt(ref.style.width),
            height: parseInt(ref.style.height),
          });
        }}
        onMouseDown={bringPanelToFront}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
          }}
        >
          {children}
        </div>
      </Rnd>
    </PanelContext.Provider>
  );
}
